import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';

// Status colors for different opposition statuses
const STATUS_COLORS = {
  'Notice Generated': '#3b82f6', // Blue
  'Pending': '#f59e0b', // Amber
  'Completed': '#10b981', // Green
  'Overdue': '#dc2626', // Dark red
  'In Progress': '#3b82f6', // Blue
  'Submitted': '#8b5cf6', // Purple
  'Rejected': '#dc2626', // Red
  'Approved': '#10b981', // Green
  'Under Review': '#f59e0b', // Amber
  'Draft': '#6b7280', // Gray
  'Opposed': '#ef4444', // Red
  'Objected': '#ef4444', // Red
  'Registered': '#10b981', // Green
  'Abandoned': '#46c37b', // Green
  'Withdrawn': '#5c90d2', // Blue
  'Refused': '#f3b760', // Orange
  'Marked for Exam': '#f3b760', // Orange
  'Exam Report Issued': '#f3b760', // Orange
  'Formalities Chk Pass': '#46c37b', // Green
  'Formalities Chk Fail': '#d26a5c', // Red
  'Send to PRAS': '#46c37b', // Green
  'Send Back to EDP': '#d26a5c', // Red
  'Cancelled': '#d26a5c', // Red
  'Removed': '#d26a5c', // Red
  'New Application': '#5c90d2', // Blue
  'Accepted & Advertised': '#70b9eb', // Light blue
  'Advertised bef acc': '#5c90d2', // Blue
  'Send To Vienna Codification': '#d26a5c', // Red
  'Withdraw Found': '#5c90d2', // Blue
  'Rectification Filed': '#f3b760', // Orange
  'Accepted/Readvertise': '#70b9eb', // Light blue
  'ABA/Readvertised': '#70b9eb', // Light blue
  'Protection Granted': '#46c37b', // Green
  'IR Cancelled': '#d26a5c', // Red
  'Deemed to be Abandoned': '#46c37b', // Green
  'Online New Appl.': '#5c90d2', // Blue
  'INVALID (No fee received)': '#d26a5c', // Red
  'Protection Refused': '#f3b760', // Orange
  'Provisional Refusal': '#f3b760', // Orange
  'Under Division': '#5c90d2', // Blue
  'Send to Re-Advertised': '#70b9eb', // Light blue
  'Acceptance Withdrawn Process': '#d26a5c', // Red
  'Opposition Withdrawn': '#46c37b' // Green
};

const Oppositions = () => {
  const navigate = useNavigate();

  // State management
  const [oppositionsData, setOppositionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [expandedNames, setExpandedNames] = useState(new Set());

  // Helper function to render colored status badge
  const renderStatusBadge = (status) => {
    if (!status) return '-';
    const color = STATUS_COLORS[status] || '#6b7280'; // Default gray if status not found
    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: `${color}20`, color: color }}
      >
        {status}
      </span>
    );
  };

  // Helper function to render colored type badge
  const renderTypeBadge = (type) => {
    if (!type) return '-';
    // Use a different color scheme for types
    const typeColors = {
      'Inbound': '#3b82f6', // Blue
      'Outbound': '#10b981', // Green
      'Internal': '#f59e0b', // Amber
      'External': '#8b5cf6', // Purple
      'Hearing': '#ef4444', // Red
      'Examination': '#f97316', // Orange
      'Opposition': '#dc2626', // Dark red
      'Renewal': '#059669', // Dark green
      'Assignment': '#7c3aed', // Dark purple
      'Licensing': '#d97706' // Dark amber
    };
    const color = typeColors[type] || '#6b7280'; // Default gray if type not found
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
        style={{ backgroundColor: `${color}15`, color: color }}
      >
        {type}
      </span>
    );
  };

  const handleViewMore = (oppositionName, oppositionId) => {
    setExpandedNames(prev => {
      const newSet = new Set(prev);
      if (newSet.has(oppositionId)) {
        newSet.delete(oppositionId);
      } else {
        newSet.add(oppositionId);
      }
      return newSet;
    });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderOppositionName = (oppositionName, oppositionId) => {
    if (!oppositionName) return '-';
    if (oppositionName.length <= 50) {
      return oppositionName;
    }
    
    const isExpanded = expandedNames.has(oppositionId);
    
    return (
      <span>
        {isExpanded ? oppositionName : truncateText(oppositionName, 50)}
        <button
          onClick={() => handleViewMore(oppositionName, oppositionId)}
          className="text-blue-600 hover:text-blue-800 underline ml-1 text-xs"
        >
          {isExpanded ? 'View Less' : 'View More'}
        </button>
      </span>
    );
  };

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(10);
  const [offset, setOffset] = useState(0);

  // Sorting
  const [sortDirection, setSortDirection] = useState('asc');

  // Filters
  const [filters, setFilters] = useState({
    trademark_appno: '',
    tmAppliedFor: '',
    buisnessName: '',
    status: [],
    tm_status: [],
    type: '',
    opp_number: '',
    opp_agentname: '',
    opp_name: ''
  });

  const [filtersApplied, setFiltersApplied] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch oppositions data from server
  const fetchOppositionsData = useCallback(async (pageOffset = offset) => {
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Fetching oppositions data...');

      const url = `https://agent3.tmpilot.com/api/global/oppositions?offset=${pageOffset}&limit=${limit}&sortBy=days_left&dir=${sortDirection}`;
      console.log('🔗 Request URL:', url);

      const response = await api.post('/api-data/get-data', {
        url: url
      });

      if (response.data.success) {
        console.log('✅ Oppositions data received:', response.data.data);
        const data = response.data.data;

        if (data.response.data && Array.isArray(data.response.data)) {
          setOppositionsData(data.response.data);
          setTotalCount(data.response.total || 0);
          setLastFetched(new Date());
          toast.success(`Loaded ${data.response.data.length} opposition records`);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error(response.data.message || 'Failed to load oppositions data');
      }
    } catch (error) {
      console.error('Error fetching oppositions:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load oppositions data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [limit, offset]);

  // Separate function for fetching data with filters
  const fetchOppositionsDataWithFilters = useCallback(async (pageOffset = offset) => {
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Fetching oppositions data with pagination and filters...');

      // Build URL with filters
      let url = `https://agent3.tmpilot.com/api/global/oppositions?offset=${pageOffset}&limit=${limit}&sortBy=days_left&dir=${sortDirection}`;

      // Add filters to URL if they have values
      Object.keys(filters).forEach(key => {
        if (Array.isArray(filters[key]) && filters[key].length > 0) {
          // Handle array fields (like status, tm_status)
          filters[key].forEach(value => {
            url += `&filters[${key}][]=${encodeURIComponent(value)}`;
          });
        } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
          // Handle string fields
          url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
        }
      });

      console.log('🔗 Request URL:', url);

      const response = await api.post('/api-data/get-data', {
        url: url
      });

      if (response.data.success) {
        console.log('✅ Oppositions data received:', response.data.data);
        const data = response.data.data;

        if (data.response.data && Array.isArray(data.response.data)) {
          setOppositionsData(data.response.data);
          setTotalCount(data.response.total || 0);
          setLastFetched(new Date());
          toast.success(`Loaded ${data.response.data.length} opposition records`);
        } else {
          throw new Error('Invalid data format received from API');
        }
      } else {
        throw new Error(response.data.message || 'Failed to load oppositions data');
      }
    } catch (error) {
      console.error('Error fetching oppositions:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load oppositions data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [limit, offset, filters, sortDirection]);

  const testApiConnection = async () => {
    setTesting(true);
    try {
      console.log('🧪 Testing API connection...');
      const response = await api.post('/api-data/get-data', {
        url: 'https://agent3.tmpilot.com/api/global/oppositions?offset=0&limit=1'
      });

      if (response.data.success) {
        toast.success('API connection test successful!');
        console.log('✅ API test successful:', response.data.data);
      } else {
        toast.error(response.data.message || 'API connection test failed');
      }
    } catch (error) {
      console.error('API test error:', error);
      toast.error(error.response?.data?.message || error.message || 'API connection test failed');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    fetchOppositionsData();
  }, [fetchOppositionsData]);

  // Handle pagination
  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * limit;
    setCurrentPage(newPage);
    setOffset(newOffset);

    // Use appropriate fetch function based on whether filters are applied
    if (filtersApplied) {
      fetchOppositionsDataWithFilters(newOffset);
    } else {
      fetchOppositionsData(newOffset);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle status multi-select changes
  const handleStatusChange = (statusValue, checked) => {
    setFilters(prev => ({
      ...prev,
      status: checked
        ? [...prev.status, statusValue]
        : prev.status.filter(s => s !== statusValue)
    }));
  };

  // Handle tm_status multi-select changes
  const handleTmStatusChange = (statusValue, checked) => {
    setFilters(prev => ({
      ...prev,
      tm_status: checked
        ? [...prev.tm_status, statusValue]
        : prev.tm_status.filter(s => s !== statusValue)
    }));
  };

  // Handle sort direction changes
  const handleSortDirectionChange = (direction) => {
    setSortDirection(direction);
    setCurrentPage(1);
    setOffset(0);

    // Make direct API call with new sort direction
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('📡 Fetching oppositions data with new sort direction...');

        // Build URL with current filters and new sort direction
        let url = `https://agent3.tmpilot.com/api/global/oppositions?offset=0&limit=${limit}&sortBy=days_left&dir=${direction}`;

        // Add filters to URL if they have values
        Object.keys(filters).forEach(key => {
          if (Array.isArray(filters[key]) && filters[key].length > 0) {
            // Handle array fields (like status, tm_status)
            filters[key].forEach(value => {
              url += `&filters[${key}][]=${encodeURIComponent(value)}`;
            });
          } else if (filters[key] && typeof filters[key] === 'string' && filters[key].trim() !== '') {
            // Handle string fields
            url += `&filters[${key}]=${encodeURIComponent(filters[key].trim())}`;
          }
        });

        console.log('🔗 Request URL:', url);

        const response = await api.post('/api-data/get-data', {
          url: url
        });

        if (response.data.success) {
          console.log('✅ Oppositions data received:', response.data.data);
          const data = response.data.data;

          if (data.response.data && Array.isArray(data.response.data)) {
            setOppositionsData(data.response.data);
            setTotalCount(data.response.total || 0);
            setLastFetched(new Date());
            toast.success(`Loaded ${data.response.data.length} opposition records`);
          } else {
            throw new Error('Invalid data format received from API');
          }
        } else {
          throw new Error(response.data.message || 'Failed to load oppositions data');
        }
      } catch (error) {
        console.error('Error fetching oppositions:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load oppositions data';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  };

  // Apply filters and fetch data from server
  const applyFilters = () => {
    setCurrentPage(1);
    setOffset(0);
    setFiltersApplied(true);
    fetchOppositionsDataWithFilters(0);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      trademark_appno: '',
      tmAppliedFor: '',
      buisnessName: '',
      status: [],
      tm_status: [],
      type: '',
      opp_number: '',
      opp_agentname: '',
      opp_name: ''
    });
    setCurrentPage(1);
    setOffset(0);
    setFiltersApplied(false);
    fetchOppositionsData(0);
  };

  // Handle application number click
  const handleAppNoClick = (appno) => {
    navigate(`/api-data/trademark-portfolio/${appno}`);
  };

  const totalPages = Math.ceil(totalCount / limit);

  // Format date to show only year
  const formatYear = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).getFullYear();
    } catch {
      return dateString.split('-')[0] || '-';
    }
  };

  // Get status color for styling
  const getStatusColor = (daysLeft) => {
    if (daysLeft < 0) return '#dc2626'; // Red for overdue
    if (daysLeft <= 7) return '#f59e0b'; // Amber for urgent
    if (daysLeft <= 30) return '#3b82f6'; // Blue for warning
    return '#10b981'; // Green for safe
  };

  // Format expire date
  const formatExpireDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch {
      return dateString;
    }
  };

  // Get days left color
  const getDaysLeftColor = (daysLeft) => {
    if (daysLeft < 0) return '#dc2626'; // Red for overdue
    if (daysLeft <= 7) return '#f59e0b'; // Amber for urgent
    if (daysLeft <= 30) return '#3b82f6'; // Blue for warning
    return '#10b981'; // Green for safe
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Oppositions</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage opposition records from external API
          </p>
        </div>

        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Sort by Days Left:</label>
            <select
              value={sortDirection}
              onChange={(e) => handleSortDirectionChange(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <button
            onClick={() => {
              if (filtersApplied) {
                fetchOppositionsDataWithFilters(offset);
              } else {
                fetchOppositionsData();
              }
            }}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <ArrowPathIcon className="h-5 w-5 mr-2" />
            )}
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>

          <button
            onClick={testApiConnection}
            disabled={testing}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {testing ? (
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
            )}
            {testing ? 'Testing...' : 'Test API'}
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900">Filters</h3>
              {filtersApplied && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Applied
                </span>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-gray-500 hover:text-gray-700"
            >
              {showFilters ? (
                <FunnelIcon className="h-5 w-5" />
              ) : (
                <FunnelIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Trademark Application Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trademark Application No
                </label>
                <input
                  type="text"
                  value={filters.trademark_appno}
                  onChange={(e) => handleFilterChange('trademark_appno', e.target.value)}
                  placeholder="Enter trademark application number"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Trademark Applied For */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trademark Applied For
                </label>
                <input
                  type="text"
                  value={filters.tmAppliedFor}
                  onChange={(e) => handleFilterChange('tmAppliedFor', e.target.value)}
                  placeholder="Enter trademark name"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name
                </label>
                <input
                  type="text"
                  value={filters.buisnessName}
                  onChange={(e) => handleFilterChange('buisnessName', e.target.value)}
                  placeholder="Enter business name"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Opposition Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opposition Status
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {['Notice Generated', 'Pending', 'Completed', 'Overdue', 'In Progress', 'Submitted', 'Rejected', 'Approved', 'Under Review', 'Draft'].map((status) => (
                    <label key={status} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.status.includes(status)}
                        onChange={(e) => handleStatusChange(status, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Trademark Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trademark Status
                </label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {['Opposed', 'Objected', 'Registered', 'Abandoned', 'Withdrawn', 'Refused', 'Marked for Exam', 'Exam Report Issued', 'Formalities Chk Pass', 'Formalities Chk Fail'].map((status) => (
                    <label key={status} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.tm_status.includes(status)}
                        onChange={(e) => handleTmStatusChange(status, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <input
                  type="text"
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  placeholder="Enter type"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Opposition Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opposition Number
                </label>
                <input
                  type="text"
                  value={filters.opp_number}
                  onChange={(e) => handleFilterChange('opp_number', e.target.value)}
                  placeholder="Enter opposition number"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Opposition Agent Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opposition Agent Name
                </label>
                <input
                  type="text"
                  value={filters.opp_agentname}
                  onChange={(e) => handleFilterChange('opp_agentname', e.target.value)}
                  placeholder="Enter opposition agent name"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              {/* Opposition Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opposition Name
                </label>
                <input
                  type="text"
                  value={filters.opp_name}
                  onChange={(e) => handleFilterChange('opp_name', e.target.value)}
                  placeholder="Enter opposition name"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Clear All
              </button>
              <button
                onClick={applyFilters}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Applying...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Data Display */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Opposition Records ({totalCount})
            </h3>
            {lastFetched && (
              <p className="text-sm text-gray-500">
                Last updated: {lastFetched.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
            <span className="ml-2 text-gray-600">Loading opposition data...</span>
          </div>
        )}

        {error && (
          <div className="px-6 py-4">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading data</h3>
                  <div className="mt-2 text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && oppositionsData.length === 0 && (
          <div className="px-6 py-12 text-center">
            <FunnelIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No opposition records found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your filters or search criteria.
            </p>
          </div>
        )}

        {!loading && !error && oppositionsData.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trademark Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Opposition Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status Information
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timeline
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {oppositionsData.map((opposition) => (
                  <tr key={opposition.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className="py-4 whitespace-nowrap text-sm text-gray-900">
                        {opposition.tmAppliedFor.replace('&amp;amp;', '&') || '-'}
                        <br></br>
                        {opposition.buisnessName || '-'}
                      </span>
                      <br></br>
                      <button
                        onClick={() => handleAppNoClick(opposition.trademark_appno)}
                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                      >
                        {opposition.trademark_appno} 
                      </button> | OppNumber: {opposition.opp_number}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span className="text-sm text-gray-900">
                        {opposition.opp_agentname || '-'}
                        <br></br>
                        <div className="break-words">
                          {renderOppositionName(opposition.opp_name, opposition.id)}
                        </div>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {renderStatusBadge(opposition.status)}
                      <br></br>
                      <span className="text-gray-500 text-xs">
                        {renderStatusBadge(opposition.tm_status)} - {renderTypeBadge(opposition.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {opposition.days_left !== null ? `${opposition.days_left} days left` : '-'}
                      <br></br>
                      <span className="text-gray-500 text-xs">
                        Expires: {formatExpireDate(opposition.expire_at)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && oppositionsData.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {offset + 1} to {Math.min(offset + limit, totalCount)} of {totalCount} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="px-3 py-2 text-sm font-medium text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Oppositions; 